import './WorkbenchPages.css';

import React, { useCallback, useEffect, useState } from 'react';

import http from '../../../api/http';
import PageHero from '../../../components/page/PageHero';

const messageOf = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
const pairText = (item) => `${item.fromSection} ${item.fromId} → ${item.toSection} ${item.toId}`;

const PairTable = ({ title, hint, items, total, extra }) => (
  <section className="card stack">
    <div className="row-between">
      <h2 className="section-title">{title}</h2>
      <span className="small-muted">{total} total{total > items.length ? ` · showing ${items.length}` : ''}</span>
    </div>
    <p className="small-muted">{hint}</p>
    {items.length ? (
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Child → parent</th>
              <th>{extra.label}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${pairText(item)}:${index}`}>
                <td>{pairText(item)}</td>
                <td>{extra.value(item) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : <p className="small-muted">None.</p>}
  </section>
);

const LinkHealthPage = () => {
  const [report, setReport] = useState(null);
  const [prune, setPrune] = useState(true);
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError('');
    return http.get('/edges/parity')
      .then(({ data }) => setReport(data))
      .catch((requestError) => setError(messageOf(requestError, 'Unable to load link parity.')));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function backfill(dryRun) {
    setBusy(true);
    setError('');
    try {
      const { data } = await http.post('/edges/backfill', { dryRun, prune });
      if (dryRun) {
        setPlan(data);
        setResult(null);
      } else {
        setResult(data);
        setPlan(null);
        await load();
      }
    } catch (requestError) {
      setError(messageOf(requestError, 'Backfill failed.'));
    } finally {
      setBusy(false);
    }
  }

  const mode = report?.mode || {};

  return (
    <div className="stack">
      <PageHero title="Link health" subtitle="Keep typed parent links in step with parent columns, then switch hierarchy reads to links." />
      {error ? <p className="error-text" role="alert">{error}</p> : null}
      {!report && !error ? <p>Checking links…</p> : null}

      {report ? (
        <>
          <div className="card-grid metrics-grid">
            <article className="card metric-card">
              <p className="hero-stat-label">Parent columns</p>
              <strong>{report.relations}</strong>
            </article>
            <article className="card metric-card">
              <p className="hero-stat-label">Parent links</p>
              <strong>{report.hierarchyEdges}</strong>
            </article>
            <article className="card metric-card">
              <p className="hero-stat-label">Missing links</p>
              <strong>{report.missingEdgeCount}</strong>
            </article>
            <article className="card metric-card">
              <p className="hero-stat-label">Stale links</p>
              <strong>{report.staleMirrorCount}</strong>
            </article>
            <article className="card metric-card">
              <p className="hero-stat-label">Link-only parents</p>
              <strong>{report.edgeOnlyLinkCount}</strong>
            </article>
          </div>

          <section className="card stack">
            <div className="row-between">
              <h2 className="section-title">Status</h2>
              <button className="button-secondary" type="button" onClick={load} disabled={busy}>Re-check</button>
            </div>
            <p className={report.inSync ? 'workbench-ok' : 'workbench-warning'}>
              {report.inSync ? 'Parent links match parent columns.' : 'Parent links have drifted from parent columns.'}
            </p>
            <p>
              Dual-write is <strong>{mode.dualWrite ? 'on' : 'off'}</strong>
              {' '}and hierarchy reads use <strong>{mode.readMode === 'edges' ? 'links only' : 'parent columns plus links'}</strong>.
            </p>
            {mode.readMode === 'edges' ? (
              <p className="small-muted">
                Hierarchy reads (lineage, health, scenarios, forecasts, cadence, capacity, &quot;why&quot;, rule parent targets, child lists, roll-ups, reports and point-in-time history) follow links only, so any drift shown here changes what they see. Set <code>EDGE_READ_MODE=union</code> to fall back to parent columns.
              </p>
            ) : report.readyForEdgeReads ? (
              <p className="small-muted">
                Ready to switch: set <code>EDGE_READ_MODE=edges</code> on the backend and restart. Set it back to <code>union</code> to revert.
              </p>
            ) : (
              <p className="small-muted">
                {mode.dualWrite
                  ? 'Backfill and prune until missing and stale counts are zero before switching to edge reads.'
                  : 'Dual-write is off (EDGE_DUAL_WRITE=off). Turn it on before switching to edge reads.'}
              </p>
            )}

            <div className="workbench-actions">
              <label>
                <input type="checkbox" checked={prune} onChange={(event) => setPrune(event.target.checked)} />
                {' '}Also remove stale links
              </label>
              <button className="button-secondary" type="button" disabled={busy} onClick={() => backfill(true)}>Preview repair</button>
              <button className="button-primary" type="button" disabled={busy || report.inSync} onClick={() => backfill(false)}>Repair now</button>
            </div>
            {plan ? (
              <div className="stack">
                <p>Repair would create <strong>{plan.wouldCreate}</strong> link(s) and remove <strong>{plan.wouldPrune}</strong> stale link(s).</p>
                {plan.sample?.length ? <pre className="workbench-pre">{plan.sample.map(pairText).join('\n')}</pre> : null}
                {plan.pruneSample?.length ? <pre className="workbench-pre">{plan.pruneSample.map((item) => `remove ${pairText(item)}`).join('\n')}</pre> : null}
              </div>
            ) : null}
            {result ? <p className="workbench-ok">Created {result.created} link(s), removed {result.pruned} stale link(s).</p> : null}
          </section>

          <PairTable
            title="Missing links"
            hint="Records whose parent column has no matching parent link."
            items={report.missingEdges || []}
            total={report.missingEdgeCount}
            extra={{ label: 'Parent column', value: (item) => item.foreignKey }}
          />
          <PairTable
            title="Stale links"
            hint="Links mirrored from a parent column that no longer holds that value (for example after a bulk update)."
            items={report.staleMirrors || []}
            total={report.staleMirrorCount}
            extra={{ label: 'Mirrored from', value: (item) => item.meta?.mirroredFrom }}
          />
          <PairTable
            title="Link-only parents"
            hint="Parent links made by hand with no parent column behind them. These are kept, and are only visible to hierarchy reads through links."
            items={report.edgeOnlyLinks || []}
            total={report.edgeOnlyLinkCount}
            extra={{ label: 'Created', value: (item) => (item.createdAt ? new Date(item.createdAt).toLocaleString() : null) }}
          />
        </>
      ) : null}
    </div>
  );
};

export default LinkHealthPage;
